import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axiosInstance from './axiosInstance';

const formatCurrency = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

const humanReadableSize = (sizeInBytes = 0) => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return '0 KB';
  const sizeInKb = sizeInBytes / 1024;
  if (sizeInKb < 1024) return `${sizeInKb.toFixed(2)} KB`;
  return `${(sizeInKb / 1024).toFixed(2)} MB`;
};

const parseErrorMessage = async (error) => {
  const fallback = 'Failed to upload the Paint & Consumables report.';
  if (!error?.response) return error?.message || fallback;

  const { data } = error.response;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed?.error || parsed?.message || text || fallback;
      } catch (_parseError) {
        return text || fallback;
      }
    } catch (_blobError) {
      return fallback;
    }
  }

  return error.response?.data?.error || error.response?.data?.message || fallback;
};

const buildBudgetPeriods = ({ pastMonths = 12, futureMonths = 12 } = {}) => {
  const periods = [];
  const currentDate = new Date();
  for (let offset = -pastMonths; offset <= futureMonths; offset += 1) {
    const periodDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1);
    periods.push({
      label: periodDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      month: periodDate.getMonth() + 1,
      year: periodDate.getFullYear(),
      value: `${periodDate.getFullYear()}-${String(periodDate.getMonth() + 1).padStart(2, '0')}`,
    });
  }
  return periods;
};

const PaintConsumablesUpload = () => {
  const budgetPeriodOptions = useMemo(() => buildBudgetPeriods(), []);
  const defaultBudgetPeriod = budgetPeriodOptions.find((period) => {
    const today = new Date();
    return period.month === today.getMonth() + 1 && period.year === today.getFullYear();
  }) || budgetPeriodOptions[budgetPeriodOptions.length - 1] || null;

  const allSitesOption = useMemo(() => ({ location_id: '', name: 'All Sites' }), []);
  const [budgetPeriod, setBudgetPeriod] = useState(defaultBudgetPeriod);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(allSitesOption);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await axiosInstance.get('/api/v1/location');
        setLocations(
          Array.isArray(response?.data?.data)
            ? [...response.data.data].sort((a, b) => {
                const nameA = (a?.name || a?.location_id || '').toString().toLowerCase();
                const nameB = (b?.name || b?.location_id || '').toString().toLowerCase();
                return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
              })
            : []
        );
      } catch (error) {
        console.error('Failed to load locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  const locationOptions = useMemo(() => [allSitesOption, ...locations], [allSitesOption, locations]);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections?.length) {
      setErrorMessage('Only .csv and .xlsx files are supported.');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;
    setSelectedFile(file);
    setSuccessMessage('');
    setErrorMessage('');
    setSummary(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Please choose a Paint & Consumables CSV or XLSX file before uploading.');
      return;
    }
    if (!budgetPeriod) {
      setErrorMessage('Please select a budget period.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('budget_month', String(budgetPeriod.month));
    formData.append('budget_year', String(budgetPeriod.year));
    if (selectedLocation?.location_id) {
      formData.append('location_id', selectedLocation.location_id);
    }

    setIsUploading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await axiosInstance.post('/api/v1/paint-consumables/report', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const payload = response?.data?.data ?? response?.data ?? null;
      setSummary(payload);
      const successCount = Array.isArray(payload?.successful_records) ? payload.successful_records.length : 0;
      const failedCount = Array.isArray(payload?.failed_records) ? payload.failed_records.length : 0;
      const periodLabel = payload?.budget_period_label || budgetPeriod.label;
      setSuccessMessage(
        `Upload completed for ${periodLabel}. ${successCount} site rows saved, ${failedCount} rows skipped.`
      );
      setSelectedFile(null);
    } catch (error) {
      const message = await parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setSuccessMessage('');
    setErrorMessage('');
    setSummary(null);
  };

  const rows = Array.isArray(summary?.successful_records) ? summary.successful_records : [];
  const failedRows = Array.isArray(summary?.failed_records) ? summary.failed_records : [];

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: '5%',
        py: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'left', width: '100%' }}>
        Upload Paint & Consumables Report
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
        Upload the monthly Paint & Consumables CSV or XLSX file. Each row is saved against the selected budget month and site.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: '100%', mb: 3 }}>
        <Autocomplete
          options={budgetPeriodOptions}
          value={budgetPeriod}
          onChange={(_, newValue) => setBudgetPeriod(newValue)}
          getOptionLabel={(option) => (option ? option.label : '')}
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          sx={{ flex: 1, minWidth: 260 }}
          renderInput={(params) => <TextField {...params} label="Budget Period" placeholder="Select month and year" />}
        />
        <Autocomplete
          options={locationOptions}
          loading={loadingLocations}
          value={selectedLocation}
          onChange={(_, newValue) => setSelectedLocation(newValue || allSitesOption)}
          getOptionLabel={(option) => (option ? option.name || option.location_id : '')}
          isOptionEqualToValue={(option, value) => option.location_id === value?.location_id}
          sx={{ flex: 1, minWidth: 260 }}
          renderInput={(params) => <TextField {...params} label="Site" placeholder="All Sites" />}
        />
      </Stack>

      <Box
        {...getRootProps({ className: 'dropzone' })}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'primary.light' : '#fff',
          cursor: 'pointer',
          width: '100%',
          minHeight: 180,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop the CSV or XLSX file here...' : 'Drag & drop the CSV or XLSX file here'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          or click to select a file
        </Typography>
      </Box>

      {selectedFile && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="info">
            Selected file: {selectedFile.name} ({humanReadableSize(selectedFile.size)})
          </Alert>
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleUpload} disabled={isUploading}>
          {isUploading ? 'Uploading…' : 'Upload Report'}
        </Button>
        <Button variant="outlined" onClick={handleClear} disabled={isUploading && !selectedFile}>
          Clear
        </Button>
      </Stack>

      {successMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="success">{successMessage}</Alert>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {rows.length > 0 && (
        <Box sx={{ mt: 3, width: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Saved Rows ({rows.length})
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Consumables</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Paint</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fleet</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Car Hire</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Budget Month</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Budget Year</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row?.site_key || row?.site_name || 'site'}-${index}`}>
                    <TableCell>{row?.site_name || '-'}</TableCell>
                    <TableCell>{formatCurrency.format(Number(row?.consumables || 0))}</TableCell>
                    <TableCell>{formatCurrency.format(Number(row?.paint || 0))}</TableCell>
                    <TableCell>{formatCurrency.format(Number(row?.fleet || 0))}</TableCell>
                    <TableCell>{formatCurrency.format(Number(row?.car_hire || 0))}</TableCell>
                    <TableCell>{summary?.budget_period_label || '-'}</TableCell>
                    <TableCell>{summary?.budget_year || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {failedRows.length > 0 && (
        <Box sx={{ mt: 3, width: '100%' }}>
          <Alert severity="warning">Failed rows: {failedRows.length}</Alert>
        </Box>
      )}
    </Box>
  );
};

export default PaintConsumablesUpload;
